import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Pencil, Search, Check, ChevronsUpDown, Trash2, FileDown, Loader2, Filter, X, CheckSquare, RefreshCw } from 'lucide-react';
import { SkorRozeti } from './EkipmanZekasiDetay';
import { SortSelect } from '../../components/SortSelect';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Checkbox } from '../../components/ui/checkbox';
import { ExportDialog } from '../../components/ExportDialog';
import { useServerList } from '../../hooks/useServerList';
import Pagination from '../../components/Pagination';
import { exportTableToPDF } from '../../utils/html2pdfExport';
import { ETIKET_PDF, DURUM_PDF } from '../../utils/pkPdfStyles';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { TESIS_TURU_OPTIONS as TESIS_TURU_SHARED, tesisTuruText, tesisTuruLabel } from '../../utils/tesisTuru';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURUM_TR = { aktif: 'Aktif', kusursuz: 'Kusursuz', hafif_kusurlu: 'Hafif Kusurlu', kusurlu: 'Kusurlu', guvenisiz: 'Güvensiz', belirsiz: 'Belirsiz' };
const KONTROL_TR = { gecmis: 'Süresi geçmiş', yaklasan: '30 gün içinde', uygun: '30 günden uzak' };
// PDF hücre renkleri (sayfadaki rozetlerle aynı)
const EXPORT_COLUMNS = [
  { id: 'sira', label: '#' }, { id: 'bina', label: 'Bina' }, { id: 'tesis_turu', label: 'Tesis Türü' }, { id: 'tip', label: 'Ekipman Tipi' },
  { id: 'alt_tip', label: 'Alt Tip' }, { id: 'marka', label: 'Marka' }, { id: 'model', label: 'Model' }, { id: 'seri_no', label: 'Seri No' },
  { id: 'asansor_kimlik_no', label: 'Asansör Kimlik No' }, { id: 'uretim_yili', label: 'Üretim Yılı' }, { id: 'kurulum', label: 'Kurulum Tarihi' },
  { id: 'periyot', label: 'Periyot (ay)' }, { id: 'son_kontrol', label: 'Son Kontrol' }, { id: 'sonraki_kontrol', label: 'Sonraki Kontrol' },
  { id: 'kalan', label: 'Kalan Gün' }, { id: 'etiket', label: 'Etiket' }, { id: 'durum', label: 'Durum' }, { id: 'saglik', label: 'Sağlık Skoru' },
];
const DEFAULT_EXPORT_COLS = EXPORT_COLUMNS.map(c => c.id).filter(id => !['tesis_turu', 'alt_tip', 'kurulum', 'uretim_yili'].includes(id));

const EKIPMAN_TIPLERI = [
  { value: 'asansor', label: 'Asansör', periyot: 12 },
  { value: 'jenerator', label: 'Jeneratör', periyot: 12 },
  { value: 'ups', label: 'UPS', periyot: 12 },
  { value: 'trafo', label: 'Trafo', periyot: 12 },
  { value: 'yangin_algilama', label: 'Yangın Algılama', periyot: 12 },
  { value: 'sprinkler', label: 'Sprinkler', periyot: 12 },
  { value: 'duman_tahliye', label: 'Duman Tahliye', periyot: 12 },
  { value: 'hvac_klima', label: 'HVAC / Klima', periyot: 12 },
  { value: 'buhar_kazani', label: 'Buhar Kazanı', periyot: 12 },
  { value: 'lpg_tanki', label: 'LPG Tankı', periyot: 120 },
  { value: 'forklift', label: 'Forklift', periyot: 12 },
  { value: 'yapi_iskelesi', label: 'Yapı İskelesi', periyot: 6 },
  { value: 'yuruyen_merdiven', label: 'Yürüyen Merdiven', periyot: 12 },
  { value: 'topraklama', label: 'Topraklama', periyot: 12 },
  { value: 'paratoner', label: 'Paratoner', periyot: 12 },
  { value: 'katodik_koruma', label: 'Katodik Koruma', periyot: 12 },
  { value: 'su_deposu', label: 'Su Deposu', periyot: 12 },
  { value: 'kompanzasyon', label: 'Kompanzasyon', periyot: 12 },
  { value: 'boyler', label: 'Boyler', periyot: 12 },
  { value: 'genlesme_tanki', label: 'Genleşme Tankı', periyot: 12 },
  { value: 'kazan', label: 'Kazan', periyot: 12 },
  { value: 'brulor', label: 'Brülör', periyot: 12 },
  { value: 'kum_filtresi', label: 'Kum Filtresi', periyot: 12 },
  { value: 'diger', label: 'Diğer', periyot: 12 }
];

const ETIKET_CONFIG = {
  kirmizi: { bg: 'bg-red-500', text: 'text-white', label: 'Kırmızı' },
  sari: { bg: 'bg-yellow-500', text: 'text-black', label: 'Sarı' },
  mavi: { bg: 'bg-blue-500', text: 'text-white', label: 'Mavi' },
  yesil: { bg: 'bg-green-500', text: 'text-white', label: 'Yeşil' }
};

const EMPTY_FORM = {
  bina_id: '', ekipman_tipi: '', alt_tip: '', marka: '', model: '', seri_no: '',
  uretim_yili: '', kurulum_tarihi: '', periyodik_sure_ay: 12,
  son_kontrol_tarihi: '', asansor_kimlik_no: '', etiket_rengi: ''
};

// Emlak modülü ile birebir aynı tesis türleri (ortak util, PKBinalar ile senkronize)
const TESIS_TURU_OPTIONS = TESIS_TURU_SHARED.map(o => ({ v: o.value, l: o.label }));

export default function PKEkipmanlar() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [binalar, setBinalar] = useState([]);
  const [binalarLoading, setBinalarLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({...EMPTY_FORM});
  const [submitting, setSubmitting] = useState(false);
  const [senkron, setSenkron] = useState(false);

  const raporlarlaEsitle = async () => {
    if (!window.confirm('Tüm ekipmanların durum, etiket ve kontrol tarihleri Periyodik Kontrol raporlarındaki EN GÜNCEL rapora göre yeniden hesaplanacak. Devam edilsin mi?')) return;
    setSenkron(true);
    try {
      const res = await axios.post(`${API}/pk/ekipmanlar/raporlarla-esitle`, {}, { withCredentials: true });
      const d = res.data;
      toast.success(d.guncellenen ? `${d.guncellenen} ekipman raporlarla eşitlendi (${d.toplam} ekipman tarandı)` : `Tüm ekipmanlar zaten raporlarla uyumlu (${d.toplam} ekipman)`, { description: d.raporsuz ? `${d.raporsuz} ekipmanın hiç raporu yok — manuel değerleri korundu` : undefined });
      ekipmanList.refetch();
    } catch (e) {
      toast.error('Eşitleme başarısız: ' + (e.response?.data?.detail || e.message));
    } finally { setSenkron(false); }
  };
  // Sıralama dropdown'u value <-> sort string mapping
  const SORT_MAP = {
    newest: 'created_at:desc',
    oldest: 'created_at:asc',
    marka_az: 'marka:asc',
    marka_za: 'marka:desc',
    tip_az: 'ekipman_tipi:asc',
    seri_az: 'seri_no:asc',
  };
  const [sortKey, setSortKey] = useState('newest');

  const ekipmanList = useServerList('/pk/ekipmanlar', {
    pageSize: 100,
    initialSort: 'created_at:desc',
    initialFilters: {},
  });

  // sortKey değiştiğinde server sort'u güncelle
  useEffect(() => {
    ekipmanList.setSort(SORT_MAP[sortKey] || 'created_at:desc');
  }, [sortKey]);// eslint-disable-line

  useEffect(() => {
    const fetchBinalar = async () => {
      try {
        const res = await axios.get(`${API}/pk/binalar`, { withCredentials: true });
        setBinalar(res.data || []);
      } catch (err) { console.error(err); }
      finally { setBinalarLoading(false); }
    };
    fetchBinalar();
  }, []);

  const handleSubmit = async () => {
    if (!formData.bina_id || !formData.ekipman_tipi || !formData.seri_no) return;
    setSubmitting(true);
    try {
      if (editMode) {
        const res = await axios.put(`${API}/pk/ekipmanlar/${editId}`, formData, { withCredentials: true });
        toast.success(res.data?.etiket_degisti ? `Ekipman güncellendi — etiket: ${ETIKET_CONFIG[res.data.etiket_rengi]?.label || 'kaldırıldı'}` : 'Ekipman güncellendi');
      } else {
        await axios.post(`${API}/pk/ekipmanlar`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      setFormData({...EMPTY_FORM});
      ekipmanList.refetch();
    } catch (err) { alert('Hata: ' + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  const handleEdit = (ek) => {
    setFormData(ek);
    setEditId(ek.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (ek) => {
    if (!window.confirm(`"${getTipLabel(ek.ekipman_tipi)} - ${ek.seri_no || ''}" ekipmanını silmek istediğinizden emin misiniz?\nBu işlem geri alınamaz.`)) return;
    try {
      await axios.delete(`${API}/pk/ekipmanlar/${ek.id}`, { withCredentials: true });
      ekipmanList.refetch();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      alert('Silme başarısız: ' + detail);
    }
  };

  const getBinaAdi = (binaId) => binalar.find(b => b.id === binaId)?.bina_adi || '—';

  const getBinaTesisTuru = (binaId) => binalar.find(b => b.id === binaId)?.tesis_turu || '';

  const getTipLabel = (tip) => EKIPMAN_TIPLERI.find(t => t.value === tip)?.label || tip;

  // Server-side: items, filters, search direkt hook'tan geliyor
  const items = ekipmanList.items;

  // Filtre setter helper
  const setFilterTip = (v) => ekipmanList.setFilter('ekipman_tipi', v || undefined);
  const setFilterBina = (v) => ekipmanList.setFilter('bina_id', v || undefined);
  const setFilterTesisTuru = (v) => ekipmanList.setFilter('tesis_turu', v === 'all' ? undefined : v);
  const setFilterEtiket = (v) => ekipmanList.setFilter('etiket_rengi', v === 'all' ? undefined : v);
  const setFilterDurum = (v) => ekipmanList.setFilter('durum', v === 'all' ? undefined : v);
  const setFilterKontrol = (v) => ekipmanList.setFilter('kontrol', v === 'all' ? undefined : v);

  const filterTip = ekipmanList.filters.ekipman_tipi || '';
  const filterBina = ekipmanList.filters.bina_id || '';
  const filterTesisTuru = ekipmanList.filters.tesis_turu || 'all';
  const filterEtiket = ekipmanList.filters.etiket_rengi || 'all';
  const filterDurum = ekipmanList.filters.durum || 'all';
  const filterKontrol = ekipmanList.filters.kontrol || 'all';
  const search = ekipmanList.search;
  const setSearch = ekipmanList.setSearch;
  const aktifFiltreSayisi = Object.values(ekipmanList.filters).filter(Boolean).length + (search?.trim() ? 1 : 0);

  // ---- Satır seçimi (sayfalar arası korunur) ----
  const [selected, setSelected] = useState({});
  const selectedCount = Object.keys(selected).length;
  const toggleSelect = (ek) => setSelected(prev => { const n = { ...prev }; if (n[ek.id]) delete n[ek.id]; else n[ek.id] = ek; return n; });
  const pageAllSelected = items.length > 0 && items.every(ek => selected[ek.id]);
  const togglePage = () => setSelected(prev => {
    const n = { ...prev };
    if (pageAllSelected) items.forEach(ek => delete n[ek.id]); else items.forEach(ek => { n[ek.id] = ek; });
    return n;
  });
  const siraNo = (idx) => (ekipmanList.page - 1) * ekipmanList.pageSize + idx + 1;

  // ---- Dışa aktarım (sütun seçimi, A4/A3, seçili / filtrelenmiş tümü) ----
  const [exportOpen, setExportOpen] = useState(false);
  const [exportCols, setExportCols] = useState(DEFAULT_EXPORT_COLS);
  const [pdfPageSize, setPdfPageSize] = useState('a3');
  const [exportScope, setExportScope] = useState('filtre');
  const [exporting, setExporting] = useState('');
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR') : '-');
  const kalanGunOf = (ek) => (ek.sonraki_kontrol_tarihi ? Math.ceil((new Date(ek.sonraki_kontrol_tarihi) - new Date()) / 86400000) : null);
  const rowValues = (ek, i) => ({
    sira: i + 1,
    bina: getBinaAdi(ek.bina_id),
    tesis_turu: tesisTuruText(getBinaTesisTuru(ek.bina_id)) || '-',
    tip: getTipLabel(ek.ekipman_tipi),
    alt_tip: ek.alt_tip || '-',
    marka: ek.marka || '-',
    model: ek.model || '-',
    seri_no: ek.seri_no || '-',
    asansor_kimlik_no: ek.asansor_kimlik_no || '-',
    uretim_yili: ek.uretim_yili || '-',
    kurulum: fmtDate(ek.kurulum_tarihi),
    periyot: ek.periyodik_sure_ay ?? '-',
    son_kontrol: fmtDate(ek.son_kontrol_tarihi),
    sonraki_kontrol: fmtDate(ek.sonraki_kontrol_tarihi),
    kalan: (() => { const k = kalanGunOf(ek); return k === null ? '-' : k <= 0 ? `${Math.abs(k)} gün geçmiş` : `${k} gün`; })(),
    etiket: ETIKET_CONFIG[ek.etiket_rengi]?.label || '-',
    durum: DURUM_TR[ek.durum] || ek.durum || '-',
    saglik: ek.ai_health_score ?? '-',
  });
  const exportScopeLabel = () => {
    if (exportScope === 'secili') return `Seçili ${selectedCount} kayıt`;
    const parts = [];
    if (filterBina) parts.push(getBinaAdi(filterBina));
    if (filterTip) parts.push(getTipLabel(filterTip));
    if (filterTesisTuru !== 'all') parts.push(filterTesisTuru === 'bos' ? 'Tesis türü boş' : tesisTuruLabel(filterTesisTuru));
    if (filterEtiket !== 'all') parts.push(`Etiket: ${filterEtiket === 'yok' ? 'Yok' : ETIKET_CONFIG[filterEtiket]?.label}`);
    if (filterDurum !== 'all') parts.push(`Durum: ${DURUM_TR[filterDurum] || filterDurum}`);
    if (filterKontrol !== 'all') parts.push(`Kontrol: ${KONTROL_TR[filterKontrol]}`);
    if (search?.trim()) parts.push(`"${search.trim()}"`);
    return parts.length ? parts.join(' · ') : 'Tüm Ekipmanlar';
  };
  const collectRows = async () => {
    if (exportScope === 'secili') {
      if (!selectedCount) { toast.error('Seçili kayıt yok — listeden kutucukları işaretleyin'); return []; }
      // Seçim anındaki kopyalar bayatlamış olabilir → güncel veriyi sunucudan al
      const res = await axios.get(`${API}/pk/ekipmanlar`, { params: { all: 1, sort: ekipmanList.sort }, withCredentials: true });
      const fresh = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const ids = new Set(Object.keys(selected));
      const sirali = fresh.filter(ek => ids.has(ek.id));
      const eksik = Object.keys(selected).filter(id => !sirali.some(ek => ek.id === id)).map(id => selected[id]);
      return sirali.concat(eksik);
    }
    return ekipmanList.fetchAll();
  };
  const buildTable = (rows) => {
    const cols = EXPORT_COLUMNS.filter(c => exportCols.includes(c.id));
    const headers = cols.map(c => c.label);
    const body = rows.map((ek, i) => { const v = rowValues(ek, i); return cols.map(c => String(v[c.id])); });
    return { cols, headers, body };
  };
  const exportPdf = async () => {
    if (exporting) return;
    if (!exportCols.length) { toast.error('En az bir sütun seçin'); return; }
    setExporting('pdf');
    try {
      const rows = await collectRows();
      if (!rows.length) { if (exportScope !== 'secili') toast.error('Aktarılacak kayıt bulunamadı'); return; }
      const { cols, headers, body } = buildTable(rows);
      const etiketIdx = cols.findIndex(c => c.id === 'etiket');
      const durumIdx = cols.findIndex(c => c.id === 'durum');
      const kalanIdx = cols.findIndex(c => c.id === 'kalan');
      const cellStyler = (r, c, raw) => {
        if (c === etiketIdx) return ETIKET_PDF[raw] || null;
        if (c === durumIdx) return DURUM_PDF[raw] || null;
        if (c === kalanIdx && typeof raw === 'string' && raw.includes('geçmiş')) return { textColor: [220, 38, 38] };
        return null;
      };
      await exportTableToPDF(headers, body, 'Ekipman Yönetimi Listesi', `Ekipman-Listesi-${new Date().toISOString().slice(0, 10)}.pdf`,
        { subtitle: `${exportScopeLabel()} · ${rows.length} kayıt`, orientation: 'landscape', pageSize: pdfPageSize, cellStyler });
      toast.success(`PDF indirildi (${rows.length} kayıt, ${pdfPageSize.toUpperCase()})`);
      setExportOpen(false);
    } catch (e) { toast.error('PDF oluşturulamadı: ' + (e?.message || e)); }
    finally { setExporting(''); }
  };
  const exportExcel = async () => {
    if (exporting) return;
    if (!exportCols.length) { toast.error('En az bir sütun seçin'); return; }
    setExporting('xlsx');
    try {
      const rows = await collectRows();
      if (!rows.length) { if (exportScope !== 'secili') toast.error('Aktarılacak kayıt bulunamadı'); return; }
      const { cols } = buildTable(rows);
      const data = rows.map((ek, i) => { const v = rowValues(ek, i); return Object.fromEntries(cols.map(c => [c.label, v[c.id]])); });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Ekipmanlar');
      XLSX.writeFile(wb, `Ekipman-Listesi-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Excel indirildi (${rows.length} kayıt)`);
      setExportOpen(false);
    } catch (e) { toast.error('Excel oluşturulamadı: ' + (e?.message || e)); }
    finally { setExporting(''); }
  };
  const openExport = (scope) => { setExportScope(scope); setExportOpen(true); };

  if (binalarLoading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="pk-ekipmanlar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Ekipman Yönetimi</h2>
          <p className="text-sm text-gray-400">{ekipmanList.total} ekipman {aktifFiltreSayisi > 0 && `· ${aktifFiltreSayisi} filtre aktif`} {ekipmanList.loading && '· yükleniyor...'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!user?.is_viewer && (
            <>
              {selectedCount > 0 && (
                <Button variant="outline" onClick={() => openExport('secili')} disabled={!!exporting} className="border-[#E67E22]/60 text-[#E67E22] hover:bg-[#E67E22]/10 h-9" title="Yalnızca işaretlediğiniz kayıtları PDF/Excel olarak indir" data-testid="ekipman-secili-export-btn">
                  <CheckSquare className="w-4 h-4 mr-2" /> Seçilileri Yazdır ({selectedCount})
                </Button>
              )}
              <Button variant="outline" onClick={raporlarlaEsitle} disabled={senkron} className="border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/20 h-9" title="Tüm ekipmanların durum/etiket/kontrol tarihlerini Periyodik Kontrol raporlarıyla eşitler" data-testid="ekipman-esitle-btn">
                {senkron ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Raporlarla Eşitle
              </Button>
              <Button variant="outline" onClick={() => openExport('filtre')} disabled={!!exporting} className="border-gray-600 text-gray-200 hover:bg-gray-800 h-9" title="Filtrelenmiş tüm ekipmanları PDF/Excel olarak indir (sütun ve kağıt seçimi)" data-testid="ekipman-pdf-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} PDF / Excel İndir
              </Button>
              <Button onClick={() => { setFormData({...EMPTY_FORM}); setEditMode(false); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="add-ekipman-btn">
                <Plus className="w-4 h-4 mr-2" /> Ekipman Ekle
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filtreler */}
      <Card className="bg-gray-900/80 border-gray-700/50" data-testid="ekipman-filtre-paneli">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold text-white"><Filter className="w-4 h-4 text-[#E67E22]" /> Filtreler {aktifFiltreSayisi > 0 && <Badge className="bg-[#E67E22]/20 text-[#E67E22] text-[10px]">{aktifFiltreSayisi} aktif</Badge>}</div>
            {aktifFiltreSayisi > 0 && (
              <Button variant="ghost" size="sm" onClick={() => { ekipmanList.resetFilters(); setSortKey('newest'); }} className="h-7 text-xs text-gray-400 hover:text-white" data-testid="ekipman-filtre-temizle"><X className="w-3 h-3 mr-1" /> Filtreleri temizle</Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1 lg:col-span-2">
              <Label className="text-[11px] text-gray-400">Arama</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input placeholder="Seri no, marka, model, alt tip, asansör kimlik no…" value={search} onChange={e => setSearch(e.target.value)} className="bg-gray-800 border-gray-700 text-white pl-9 h-9 text-sm" data-testid="ekipman-arama" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Ekipman Tipi</Label>
              <Select value={filterTip || '_all'} onValueChange={v => setFilterTip(v === '_all' ? '' : v)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm" data-testid="ekipman-tip-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[320px]">
                  <SelectItem value="_all">Tümü</SelectItem>
                  {EKIPMAN_TIPLERI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Bina</Label>
              <BinaCombobox
                binalar={[{ id: '', bina_adi: 'Tüm Binalar' }, ...binalar]}
                value={filterBina}
                onChange={(v) => setFilterBina(v)}
                placeholder="Bina (Tümü)"
                testId="filter-bina-combobox"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Tesis Türü</Label>
              <Select value={filterTesisTuru} onValueChange={setFilterTesisTuru}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm" data-testid="ekipman-tesis-turu-filter"><SelectValue placeholder="Tesis Türü" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[320px]">
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="bos">(Tesis türü boş)</SelectItem>
                  {TESIS_TURU_OPTIONS.map(opt => <SelectItem key={opt.v} value={opt.v}>{opt.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Etiket</Label>
              <Select value={filterEtiket} onValueChange={setFilterEtiket}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm" data-testid="ekipman-etiket-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="all">Tümü</SelectItem>
                  {Object.entries(ETIKET_CONFIG).map(([k, c]) => <SelectItem key={k} value={k}><span className="inline-flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${c.bg}`} />{c.label}</span></SelectItem>)}
                  <SelectItem value="yok">Etiket yok</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Durum</Label>
              <Select value={filterDurum} onValueChange={setFilterDurum}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm" data-testid="ekipman-durum-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="all">Tümü</SelectItem>
                  {Object.entries(DURUM_TR).filter(([k]) => k !== 'belirsiz').map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Kontrol Durumu</Label>
              <Select value={filterKontrol} onValueChange={setFilterKontrol}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm" data-testid="ekipman-kontrol-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="all">Tümü</SelectItem>
                  {Object.entries(KONTROL_TR).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-400">Sıralama</Label>
              <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
                { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
                { value: 'marka_az', label: 'Marka (A-Z)' }, { value: 'tip_az', label: 'Ekipman Tipi (A-Z)' },
                { value: 'seri_az', label: 'Seri No (A-Z)' }
              ]} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Seçim çubuğu */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-[#E67E22]/40 bg-[#E67E22]/10 px-4 py-2 text-sm text-gray-200" data-testid="ekipman-secim-bar">
          <CheckSquare className="w-4 h-4 text-[#E67E22]" /> <b>{selectedCount}</b> ekipman seçili
          <Button variant="ghost" size="sm" onClick={() => setSelected({})} className="h-7 text-xs text-gray-400 hover:text-white ml-auto" data-testid="ekipman-secim-temizle"><X className="w-3 h-3 mr-1" /> Seçimi temizle</Button>
        </div>
      )}

      {/* Tablo */}
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-xs text-gray-400 bg-gray-800/50">
                  <th className="py-3 pl-4 pr-1 w-8">
                    <Checkbox checked={pageAllSelected} onCheckedChange={togglePage} className="border-gray-500" title="Bu sayfadakilerin tümünü seç" data-testid="ekipman-sec-tumu" />
                  </th>
                  <th className="text-right py-3 px-2 w-10">#</th>
                  <th className="text-left py-3 px-4">Bina</th>
                  <th className="text-left py-3 px-4">Tip</th>
                  <th className="text-left py-3 px-4">Marka / Model</th>
                  <th className="text-left py-3 px-4">Seri No</th>
                  <th className="text-center py-3 px-4">Periyot</th>
                  <th className="text-center py-3 px-4">Sonraki Kontrol</th>
                  <th className="text-center py-3 px-4">Etiket</th>
                  <th className="text-center py-3 px-4">Durum</th>
                  <th className="text-center py-3 px-4" title="Ekipman Zekâsı sağlık skoru">Sağlık</th>
                  <th className="text-right py-3 px-4">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !ekipmanList.loading && (
                  <tr><td colSpan={12} className="py-8 text-center text-gray-500 text-sm" data-testid="ekipman-bos">Filtrelere uyan ekipman bulunamadı.</td></tr>
                )}
                {items.map((ek, idx) => {
                  const etiketCfg = ETIKET_CONFIG[ek.etiket_rengi];
                  const kontrol = ek.sonraki_kontrol_tarihi;
                  const kalanGun = kontrol ? Math.ceil((new Date(kontrol) - new Date()) / 86400000) : null;
                  const isSel = !!selected[ek.id];
                  return (
                    <tr key={ek.id} className={`border-b border-gray-800/50 hover:bg-gray-800/30 ${isSel ? 'bg-[#E67E22]/10' : ek.etiket_rengi === 'kirmizi' ? 'bg-red-950/15' : ''}`} data-testid={`ekipman-row-${ek.id}`}>
                      <td className="py-2 pl-4 pr-1">
                        <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(ek)} className="border-gray-500" data-testid={`ekipman-sec-${ek.id}`} />
                      </td>
                      <td className="py-2 px-2 text-right text-gray-500 text-xs tabular-nums" data-testid={`ekipman-sira-${ek.id}`}>{siraNo(idx)}</td>
                      <td className="py-2 px-4 text-gray-300 text-xs">{getBinaAdi(ek.bina_id)}</td>
                      <td className="py-2 px-4 text-white text-xs">{getTipLabel(ek.ekipman_tipi)}</td>
                      <td className="py-2 px-4">
                        <p className="text-white text-xs">{ek.marka} {ek.model}</p>
                        {ek.alt_tip && <p className="text-gray-500 text-[10px]">{ek.alt_tip}</p>}
                      </td>
                      <td className="py-2 px-4 text-blue-300 font-mono text-xs">{ek.seri_no}</td>
                      <td className="py-2 px-4 text-center text-gray-400 text-xs">{ek.periyodik_sure_ay} ay</td>
                      <td className="py-2 px-4 text-center">
                        {kalanGun !== null && (
                          <span className={`text-xs ${kalanGun <= 0 ? 'text-red-400 font-bold' : kalanGun <= 30 ? 'text-orange-400' : 'text-gray-400'}`}>
                            {kalanGun <= 0 ? `${Math.abs(kalanGun)}g geçmiş` : `${kalanGun}g`}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-center">
                        {etiketCfg && <Badge className={`${etiketCfg.bg} ${etiketCfg.text} text-[10px]`}>{etiketCfg.label}</Badge>}
                      </td>
                      <td className="py-2 px-4 text-center">
                        <Badge className={`text-[10px] ${ek.durum === 'guvenisiz' ? 'bg-red-700' : ek.durum === 'kusurlu' ? 'bg-yellow-700 text-black' : ek.durum === 'hafif_kusurlu' ? 'bg-yellow-600/70 text-black' : ek.durum === 'kusursuz' ? 'bg-green-700' : 'bg-gray-700'}`}>
                          {DURUM_TR[ek.durum] || ek.durum}
                        </Badge>
                      </td>
                      <td className="py-2 px-4 text-center" data-testid={`ekipman-saglik-${ek.id}`}>
                        <SkorRozeti score={ek.ai_health_score} level={ek.ai_risk_level} />
                      </td>
                      <td className="py-2 px-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          {!user?.is_viewer && (
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(ek)} className="h-7 w-7 p-0 text-gray-400 hover:text-white" data-testid={`ekipman-duzenle-${ek.id}`}><Pencil className="w-3 h-3" /></Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(ek)}
                              className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"
                              title="Sil (Admin)"
                              data-testid={`ekipman-sil-${ek.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Server-side pagination */}
          <div className="border-t border-gray-700/40 px-3 py-2">
            <Pagination
              page={ekipmanList.page}
              totalPages={ekipmanList.totalPages}
              total={ekipmanList.total}
              pageSize={ekipmanList.pageSize}
              loading={ekipmanList.loading}
              onPageChange={ekipmanList.setPage}
              onPageSizeChange={ekipmanList.setPageSize}
              testIdPrefix="ekipman-pagination"
            />
          </div>
        </CardContent>
      </Card>

      {/* Dışa aktarım: sütun seçimi + kağıt boyutu + kapsam */}
      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        columns={EXPORT_COLUMNS}
        selectedColumns={exportCols}
        onToggleColumn={(id) => setExportCols(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])}
        onSelectAll={() => setExportCols(EXPORT_COLUMNS.map(c => c.id))}
        onClearAll={() => setExportCols([])}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportPdf}
        onExportExcel={exportExcel}
        title="Ekipman Listesi — PDF / Excel"
        description="Kapsam, sütunlar ve kağıt boyutunu seçin. PDF'te Etiket ve Durum hücreleri sayfadaki gibi renkli basılır."
        gridCols={3}
        idPrefix="ekipman-export"
      >
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-800 bg-gray-950/60 p-3" data-testid="ekipman-export-kapsam">
          <span className="text-xs text-gray-400 mr-1">Kapsam:</span>
          <Button size="sm" onClick={() => setExportScope('filtre')} className={`h-7 px-3 text-xs ${exportScope === 'filtre' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-testid="ekipman-export-kapsam-filtre">
            Filtrelenmiş tümü ({ekipmanList.total})
          </Button>
          <Button size="sm" onClick={() => setExportScope('secili')} disabled={selectedCount === 0} className={`h-7 px-3 text-xs ${exportScope === 'secili' ? 'bg-[#E67E22] text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-testid="ekipman-export-kapsam-secili">
            Seçili kayıtlar ({selectedCount})
          </Button>
          <span className="text-[11px] text-gray-500 ml-auto">{exportScopeLabel()}</span>
        </div>
      </ExportDialog>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setFormData({...EMPTY_FORM}); setEditMode(false); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="ekipman-form-title">{editMode ? 'Ekipman Düzenle' : 'Yeni Ekipman Ekle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bina *</Label>
                <BinaCombobox
                  binalar={binalar}
                  value={formData.bina_id}
                  onChange={(v) => setFormData({...formData, bina_id: v})}
                />
              </div>
              <div className="space-y-2">
                <Label>Ekipman Tipi *</Label>
                <Select value={formData.ekipman_tipi} onValueChange={v => {
                  const periyot = EKIPMAN_TIPLERI.find(t => t.value === v)?.periyot || 12;
                  setFormData({...formData, ekipman_tipi: v, periyodik_sure_ay: periyot});
                }}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Tip seçin" /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {EKIPMAN_TIPLERI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Alt Tip / Açıklama</Label>
              <Input value={formData.alt_tip} onChange={e => setFormData({...formData, alt_tip: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Hidrolik Asansör, Online UPS 200kVA" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Marka</Label>
                <Input value={formData.marka} onChange={e => setFormData({...formData, marka: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Input value={formData.model} onChange={e => setFormData({...formData, model: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Seri No *</Label>
                <Input value={formData.seri_no} onChange={e => setFormData({...formData, seri_no: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Üretim Yılı</Label>
                <Input type="number" value={formData.uretim_yili} onChange={e => setFormData({...formData, uretim_yili: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Kurulum Tarihi</Label>
                <Input type="date" value={formData.kurulum_tarihi} onChange={e => setFormData({...formData, kurulum_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Periyodik Süre (ay)</Label>
                <Input type="number" value={formData.periyodik_sure_ay} onChange={e => setFormData({...formData, periyodik_sure_ay: parseInt(e.target.value) || 12})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Son Kontrol Tarihi</Label>
              <Input type="date" value={formData.son_kontrol_tarihi} onChange={e => setFormData({...formData, son_kontrol_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
            </div>
            {/* Asansör özel alanları */}
            {formData.ekipman_tipi === 'asansor' && (
              <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg space-y-3">
                <p className="text-xs font-semibold text-blue-400">Asansör Özel Alanları</p>
                <div className="space-y-2">
                  <Label>Asansör Kimlik No *</Label>
                  <Input value={formData.asansor_kimlik_no} onChange={e => setFormData({...formData, asansor_kimlik_no: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="34-ASN-10001" />
                </div>
                <div className="space-y-2">
                  <Label>Etiket Durumu</Label>
                  <Select value={formData.etiket_rengi || 'yok'} onValueChange={v => setFormData({...formData, etiket_rengi: v === 'yok' ? '' : v})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="ekipman-etiket-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="yok" data-testid="ekipman-etiket-opt-yok">Etiket yok</SelectItem>
                      {Object.entries(ETIKET_CONFIG).map(([k, c]) => (
                        <SelectItem key={k} value={k} data-testid={`ekipman-etiket-opt-${k}`}>
                          <span className="inline-flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${c.bg}`} />{c.label} Etiket</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-500">Etiket değişince asansör durumu ve geri sayım otomatik güncellenir; bildirim oluşturulur.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); setFormData({...EMPTY_FORM}); }} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">{submitting ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Aramali Bina Combobox (cmdk + Popover) - uzun bina listelerinde scroll/seçim sorunlarını çözer
const normalizeTr = (s) => (s || '')
  .toString()
  .toLocaleLowerCase('tr-TR')
  .replaceAll('ı', 'i').replaceAll('ş', 's').replaceAll('ğ', 'g')
  .replaceAll('ü', 'u').replaceAll('ö', 'o').replaceAll('ç', 'c')
  .replaceAll('İ', 'i').replaceAll('Ş', 's').replaceAll('Ğ', 'g')
  .replaceAll('Ü', 'u').replaceAll('Ö', 'o').replaceAll('Ç', 'c');

function BinaCombobox({ binalar, value, onChange, placeholder = 'Bina seçin', emptyText = 'Bina bulunamadı', testId = 'bina-combobox' }) {
  const [open, setOpen] = useState(false);
  const selected = binalar.find(b => b.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-gray-800 border-gray-700 text-white hover:bg-gray-800 hover:text-white font-normal"
          data-testid={testId}
        >
          <span className={selected ? 'truncate' : 'text-gray-400 truncate'}>
            {selected ? selected.bina_adi : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-gray-900 border-gray-700 text-white"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        align="start"
      >
        <Command
          className="bg-gray-900 text-white"
          filter={(value, search) => normalizeTr(value).includes(normalizeTr(search)) ? 1 : 0}
        >
          <CommandInput placeholder="Bina ara..." className="text-white placeholder:text-gray-500" data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty className="text-gray-400">{emptyText}</CommandEmpty>
            <CommandGroup>
              {binalar.map(b => (
                <CommandItem
                  key={b.id || '_all'}
                  value={b.bina_adi}
                  onSelect={() => { onChange(b.id); setOpen(false); }}
                  className="text-gray-200 aria-selected:bg-gray-800 cursor-pointer"
                  data-testid={`${testId}-item-${b.id || '_all'}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === b.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{b.bina_adi}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

